/**
 * SMBSignupForm component
 *
 * Reusable form component for SMB signup
 * Handles form fields, validation, and submission
 *
 * Visual is provided by the shared SignupCredentialsForm; this wrapper
 * adds the SMB-specific submit logic (recaptcha + signup), analytics,
 * Browse-free subtitle, and the "Already have an account? Sign in"
 * footer that toggles to the login view.
 */
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useRef } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { SignupSessionError } from '@/auth';
import Link from '@/components/Link';

import { SignupCredentialsForm } from './SignupCredentialsForm';
import { useSMBSignup } from './hooks/useSMBSignup';
import { SMBSignupFormData, SMBSignupFormProps } from './types';
import {
  calculateDuration,
  trackPhase1GetFreeAccessClicked,
  trackPhase1PrivacyPolicyClicked,
  trackPhase1SignupFailed,
  trackPhase1SignupSuccess,
  trackPhase1SwitchToLoginClicked,
  trackPhase1TermsOfUseClicked,
} from './utils/analytics';

const useStyles = makeStyles((theme: any) => ({
  login: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: theme.spacing(1), // Closer to "Browse free" text
  },
  loginLink: {
    background: 'none',
    border: 'none',
    color: 'var(--brandPrimary)', // Green button color; MUI primary stays dark in dark mode
    cursor: 'pointer',
    fontSize: 'inherit',
    padding: 0,
    fontWeight: 400, // Normal weight
    marginLeft: theme.spacing(0.5), // Space after question mark
    '&:hover': {
      opacity: 0.8,
    },
  },
}));

/* eslint-disable react/prop-types */
export const SMBSignupForm: React.FC<SMBSignupFormProps> = ({
  onSuccess,
  onError,
  onToggleView,
  onSignInRequired,
  returnTo,
  expert,
}) => {
  const s = useStyles();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { loading, error, signupSMBUser } = useSMBSignup();
  const formStartTime = useRef<Date>(new Date());
  const modalOpenTime = useRef<Date>(new Date());

  const handleSubmit = useCallback(
    async (values: SMBSignupFormData) => {
      // Track form submission
      const hasFilledAllFields =
        !!values.first_name &&
        !!values.last_name &&
        !!values.email &&
        !!values.password &&
        !!values.accept_privacy_and_terms_of_use;

      const formDuration = calculateDuration(formStartTime.current);
      trackPhase1GetFreeAccessClicked(hasFilledAllFields, formDuration, expert);

      try {
        const recaptchaToken = executeRecaptcha ? await executeRecaptcha('smb_signup') : '';
        // Track success before signupSMBUser — signup() calls window.location.assign()
        // which destroys the execution context, so tracking after the call never fires.
        const durationToComplete = calculateDuration(modalOpenTime.current);
        trackPhase1SignupSuccess(durationToComplete, expert);

        const validationErrors = await signupSMBUser(values, recaptchaToken, returnTo);

        // If we got validation errors, return them to react-final-form
        if (validationErrors) {
          // Track validation failure
          trackPhase1SignupFailed('validation', 'Form validation failed', undefined, expert);
          return validationErrors;
        }

        // Just call the onSuccess callback (unreachable on success — signup redirects)
        onSuccess('success');
      } catch (e: unknown) {
        // Track API/network failure. A SignupSessionError's message is our own
        // copy, so record the token-step failure behind it.
        const failure = e instanceof SignupSessionError ? e.cause : e;
        const errorMessage = failure instanceof Error ? failure.message : 'Unknown error';
        const errorType = errorMessage.toLowerCase().includes('network') ? 'network' : 'api';
        trackPhase1SignupFailed(errorType, errorMessage, undefined, expert);

        // The account exists, so this form can only fail again. signup() has
        // already reported it, so it skips onError too (ENG-5760).
        if (e instanceof SignupSessionError) {
          onSignInRequired?.({ email: values.email, notice: e.message });
          return undefined;
        }

        if (onError && e instanceof Error) {
          onError(e);
        }
        // Error is already set in the hook
      }
    },
    [executeRecaptcha, signupSMBUser, onSuccess, onError, onSignInRequired, returnTo, expert]
  );

  const handlePrivacyClick = useCallback(() => {
    trackPhase1PrivacyPolicyClicked(expert);
  }, [expert]);

  const handleTermsClick = useCallback(() => {
    trackPhase1TermsOfUseClicked(expert);
  }, [expert]);

  const handleSignInClick = useCallback(() => {
    const timeOnView = calculateDuration(formStartTime.current);
    trackPhase1SwitchToLoginClicked(timeOnView, expert);
    if (onToggleView) {
      onToggleView();
    }
  }, [onToggleView, expert]);

  return (
    <>
      <SignupCredentialsForm
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        submitLabel="Get Free Access"
        submittingLabel="Creating account..."
        onPrivacyClick={handlePrivacyClick}
        onTermsClick={handleTermsClick}
      />

      <Box sx={{ textAlign: 'center', mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
        Browse free. Book only when you need to.
      </Box>

      <div className={s.login}>
        Already have an account?
        {onToggleView ? (
          <button type="button" onClick={handleSignInClick} className={s.loginLink}>
            Sign in
          </button>
        ) : (
          <Link to={`/login?next=${encodeURIComponent(returnTo || '/dashboard')}`}> Sign in</Link>
        )}
      </div>
    </>
  );
};

export default SMBSignupForm;
